/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { classifyTier } from "@cosmos/airtable-data";
import { DirectoryCard } from "@cosmos/components";
import { useMessages } from "@epic-mw/i18n";

import {
	buildPartnerLinks,
	buildPartnerLogo,
	partnerBadgeLabel,
	partnerBadgeMessages,
	tierToTone,
} from "./buildPartnerCardProps";

import type { ServicePartner } from "@cosmos/airtable-data";

export interface ServicePartnerCardProps {
	data: ServicePartner;
}

/**
 * Adapter wrapping `DirectoryCard` for a `ServicePartners` row.
 *
 * Currently renders as a NON-clickable card matching `TrainingPartnerCard`'s
 * mapping plus a second tag row for the partner's offered services and an
 * extra office-location entry in the meta row. Profile-URL wiring (which
 * would route the `href` to `DirectoryCard` and surface the chevron
 * affordance) is deferred until the `/service-partners` route lands and
 * `ServicePartner` gains a `profileUrl` field on the Airtable side.
 */
export function ServicePartnerCard({ data: partner }: ServicePartnerCardProps): React.JSX.Element {
	const messages = useMessages();
	const tier = classifyTier(partner.badgeLevel);
	const badgeLabel = partnerBadgeLabel(tier, partnerBadgeMessages(messages));

	const logo = buildPartnerLogo(partner.logoUrl, tier);

	const { buttons, meta } = buildPartnerLinks(
		partner,
		{
			website: messages["epic.cosmos.directory.card.website"] ?? "Website",
			email: messages["epic.cosmos.directory.card.email"] ?? "Email",
		},
		{ officeLocation: partner.officeLocation },
	);

	return (
		<DirectoryCard
			name={partner.name}
			badge={{ label: badgeLabel, tone: tierToTone(tier) }}
			logo={logo}
			meta={meta}
			primaryTags={partner.industries}
			secondaryTags={partner.services}
			buttons={buttons}
		/>
	);
}
